import { lazy, Suspense, useCallback, useEffect, useId, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Workflow, X, AlertTriangle, ExternalLink } from 'lucide-react'
import { ensureCsrfToken } from '../../../services/csrf.js'
import { useEscapeClose } from '../../shared/hooks/useEscapeClose'
import { useScrimDismiss } from '../../shared/hooks/useScrimDismiss'
import { useDialogFocus } from '../hooks/useDialogFocus'

/**
 * « Processus » — le flux ECA qui traite les saisies de ce formulaire.
 *
 * POURQUOI CE PANNEAU EXISTE. Le lien entre un formulaire du builder et son
 * traitement serveur n'est déclaré NULLE PART : le bloc porte une URL dans ses
 * données (`contactEndpoint: /eca/landing/contact`), son `submitData` la poste,
 * et un flux ECA répond s'il déclare l'événement `eca_endpoint:response` avec
 * les mêmes arguments de chemin. Une convention de chaîne de caractères, rien de
 * plus — invisible des deux côtés. Ce panneau est ce qui la rend visible, et
 * modifiable sans éditer de YAML.
 *
 * L'ÉCRITURE VA DANS LA DÉFINITION du bloc, pas dans la place — c'est le seul
 * niveau qui ait un effet. Le `submitData` lit l'URL à la RACINE du rjBuild, où
 * le magasin d'une unité n'arrive jamais (`getPageStateUnitData` écarte les
 * unités persistables) : un réglage écrit par place paraîtrait enregistré et le
 * formulaire continuerait de poster sur l'ancienne adresse. C'est aussi la bonne
 * granularité — le partage se compte en formulaires, et rebrancher une seule des
 * quatre-vingt-dix poses d'un pied de page n'aurait pas de sens. Ce panneau
 * n'appelle donc jamais le `onSave` du registre d'éditeurs : il poste sur
 * `/api/builder/def/process`.
 *
 * `surface: 'fullscreen'` : le panneau porte son propre dialogue en portail,
 * comme la médiathèque. Un éditeur ECA dans une pop-in ancrée de 42 rem n'aurait
 * aucun sens — et le portail est ce qui l'affranchit du contexte d'empilement du
 * canevas (un bloc peut se déclarer en `z-[100]`, l'Assistant est en `z-[200]`).
 */

// Chargé à la demande : l'éditeur ECA traîne ReactFlow, dagre et ses
// convertisseurs. Importé en statique depuis le registre d'éditeurs, il partirait
// dans le chunk du builder — que toute page en édition paie, pour un panneau que
// seul un administrateur ouvre.
const EcaEditor = lazy(() => import('../../eca/EcaEditor.jsx').then(m => ({ default: m.EcaEditor })))

/** Rien à afficher : la valeur ne pointe sur aucun processus. */
const NONE = ''

/**
 * Une page qui héberge un formulaire branché sur ce processus.
 *
 * Le slug est montré À CÔTÉ du titre, et ce n'est pas de la décoration : sur un
 * site importé, l'aspiration a souvent capté le même `<title>` partout, et une
 * liste de trente lignes identiques n'aide personne à retrouver la sienne.
 */
const PageLink = ({ page }) => {
  const name = page.label || page.title || `Page ${page.nid}`
  if (!page.slug) return <span className="text-slate-700">{name}</span>
  return (
    <>
      {/* Un vrai lien, pas un bouton : la cible est une page, et l'ouverture
          d'une nouvelle fenêtre doit s'annoncer. */}
      <a
        href={`/builder/${page.slug}`}
        target="_blank"
        rel="noopener"
        // En mode édition, le wrapper de bloc annule la navigation de TOUT
        // `a[href]` (cliquer un lien d'un bloc l'édite, il ne le suit pas) —
        // depuis un `onClickCapture`, donc aucune coupure depuis ce panneau
        // n'arriverait à temps. Cet attribut est l'échappatoire prévue par
        // `handleWrapperClick` : ce lien n'est pas du contenu de bloc.
        data-builder-allow-navigation=""
        className="underline decoration-dotted underline-offset-2 hover:text-teal-700"
      >
        {name}
        <span className="sr-only"> (nouvelle fenêtre)</span>
      </a>
      <span className="ml-2 font-mono text-2xs text-slate-400">/{page.slug}</span>
    </>
  )
}

/**
 * La liste des formulaires et de leurs pages — dans SA pop-in.
 *
 * POURQUOI À PART. Le résumé du panneau tient dans un bandeau de deux lignes,
 * et onze formulaires posés sur cinquante pages n'y tiennent pas : la liste
 * poussait le sélecteur et l'avertissement hors de vue, pour une information
 * qu'on ne consulte qu'une fois. Le bandeau garde donc le seul chiffre qui
 * décide (« partagé par N formulaires »), et le détail se déplie ici.
 *
 * Requête PROPRE, à l'ouverture : le panneau interroge la même route avec
 * `limit=2` — assez pour un bandeau, pas pour une liste. Les COMPTES, eux,
 * sont exacts des deux côtés (`pagesTotal` est le total, pas la taille de la
 * tranche), si bien que le lien annonce le bon nombre avant même d'ouvrir.
 */
const ProcessFormsDialog = ({ endpoint, onClose }) => {
  const panelRef = useRef(null)
  const titleId = useId()
  const scrim = useScrimDismiss(onClose)
  // Le registre d'Échap classe par rang de rendu : cette pop-in, montée après
  // le panneau, prend la touche seule — le panneau reste ouvert derrière.
  useEscapeClose(onClose)
  useDialogFocus(panelRef)

  const [forms, setForms] = useState(null)
  const [error, setError] = useState(null)

  useEffect(() => {
    const controller = new AbortController()
    fetch(
      `/api/builder/processes?endpoint=${encodeURIComponent(endpoint)}&limit=50`,
      { credentials: 'include', signal: controller.signal },
    )
      .then(r => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then(data => setForms(data.forms ?? []))
      .catch(e => { if (e.name !== 'AbortError') setError('Impossible de lire la liste des formulaires.') })
    return () => controller.abort()
  }, [endpoint])

  return createPortal(
    <div className="modal-scrim modal-scrim-over" {...scrim}>
      <div
        ref={panelRef}
        className="modal-panel max-w-2xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onMouseDown={e => e.stopPropagation()}
      >
        <div className="modal-header">
          <h2 id={titleId} className="min-w-0 truncate">
            Formulaires branchés sur{' '}
            <code className="rounded bg-slate-100 px-1.5 py-0.5 text-sm font-normal">{endpoint}</code>
          </h2>
          <button type="button" onClick={onClose} className="btn btn-ghost" aria-label="Fermer la liste">
            <X size={18} aria-hidden="true" />
          </button>
        </div>

        <div className="modal-body flex-1 space-y-5" aria-live="polite" aria-busy={forms === null && !error}>
          {error && <p className="text-rose-700" role="alert">{error}</p>}
          {forms === null && !error && <p className="text-slate-500">Chargement…</p>}
          {forms?.length === 0 && <p className="text-slate-600">Aucun formulaire déclaré sur ce chemin.</p>}

          {forms?.map(form => (
            <section key={form.type} className="space-y-1.5">
              <h3 className="text-sm font-semibold text-slate-800">
                {form.name}
                <span className="ml-2 font-normal text-slate-500">
                  {form.pagesTotal > 0
                    ? <>{form.pagesTotal} page{form.pagesTotal > 1 ? 's' : ''}</>
                    : <>{form.instances} exemplaire{form.instances > 1 ? 's' : ''}</>}
                </span>
              </h3>
              {form.pages.length > 0 && (
                <ul className="space-y-1 text-xs">
                  {form.pages.map(page => (
                    <li key={page.nid} className="flex items-baseline gap-2 text-slate-600">
                      <PageLink page={page} />
                    </li>
                  ))}
                </ul>
              )}
              {/* La route plafonne la tranche : le dire plutôt que laisser
                  croire que la liste est complète. */}
              {form.pagesTotal > form.pages.length && (
                <p className="text-xs text-slate-500">
                  {form.pagesTotal - form.pages.length > 1
                    ? `et ${form.pagesTotal - form.pages.length} autres pages non listées.`
                    : 'et 1 autre page non listée.'}
                </p>
              )}
            </section>
          ))}
        </div>
      </div>
    </div>,
    document.body,
  )
}

const ProcessSummary = ({ state, endpoint, onShowForms }) => {
  const { current, forms, shared, kind } = state
  // « Processus » ne vaut que pour un flux ECA : une route servie par du code est
  // un traitement, pas un processus. Un seul mot, mais c'est celui qui dit au
  // lecteur ce qu'il peut aller modifier.
  const noun = kind === 'eca' ? 'processus' : 'traitement'

  // CE QUI MÉRITE UNE LIGNE : ce que la liste déroulante ne dit pas déjà. Elle
  // nomme le flux rattaché juste au-dessus — le renommer ici, avec son nom
  // machine et le flux d'accès qui le couvre, faisait deux lignes de redite
  // avant l'éditeur. Ne reste que ce qui alerte : un flux désactivé ne traite
  // rien, une adresse sans flux envoie les saisies dans le vide.
  const notice = current
    ? (!current.status && (
        <span className="badge badge-warning">Désactivé — les saisies ne sont pas traitées</span>
      ))
    : kind === 'route' ? (
      // Câblé, mais pas par un flux : une route servie par du CODE. Le dire
      // plutôt que « aucun processus » — un formulaire qui marche ne doit pas
      // s'annoncer en panne.
      <span className="text-slate-700">
        Traité par une route de l’application —{' '}
        <code className="rounded bg-slate-100 px-1.5 py-0.5 text-2xs">{endpoint}</code>. Pas de flux ECA à afficher.
      </span>
    ) : (
      <span className="text-slate-600">
        {endpoint
          ? <>Aucun flux ECA ne répond à <code className="rounded bg-slate-100 px-1.5 py-0.5 text-2xs">{endpoint}</code> : les saisies partiraient dans le vide.</>
          : 'Ce formulaire n’est relié à aucun processus.'}
      </span>
    )

  // Y a-t-il quelque chose à déplier ? Un formulaire seul et jamais posé n'a
  // pas de liste — le lien ne s'affiche alors pas plutôt que d'ouvrir un vide.
  const pagesTotal = forms.reduce((n, form) => n + (form.pagesTotal || 0), 0)
  const detail = (shared || pagesTotal > 0) && (
    <button
      type="button"
      onClick={onShowForms}
      className="underline decoration-dotted underline-offset-2 hover:text-teal-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 rounded"
    >
      {shared
        ? <>Voir les {forms.length} formulaires et leurs pages</>
        : <>Voir {pagesTotal > 1 ? `les ${pagesTotal} pages` : 'la page'} de ce formulaire</>}
    </button>
  )

  return (
    <div className="flex flex-col gap-2 text-sm">
      {notice && (
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">{notice}</div>
      )}

      {shared ? (
        <p className="alert alert-warning">
          <AlertTriangle size={15} aria-hidden="true" className="mt-0.5 shrink-0" />
          <span>
            <strong className="font-semibold">{noun === 'processus' ? 'Processus partagé' : 'Traitement partagé'} par {forms.length} formulaires.</strong>{' '}
            Le rebrancher ne touche que celui-ci ; modifier le flux ci-dessous les change tous.{' '}
            {detail}
          </span>
        </p>
      ) : (
        forms.length === 1 && (
          // « Aucun AUTRE formulaire déclaré » et non « exclusif » : le serveur
          // ne voit que les blocs qui déclarent un réglage `process`. Un bloc qui
          // posterait sur le même chemin sans le déclarer resterait invisible —
          // le dire évite une fausse assurance sur le seul point qui compte.
          <p className="text-xs text-slate-600">
            Aucun autre formulaire déclaré sur ce {noun}.{detail && <> {detail}</>}
          </p>
        )
      )}
    </div>
  )
}

export const ProcessEditor = ({ value, label = 'Processus', defNid, field, seedEndpoint, onClose }) => {
  // La source de vérité est la valeur portée par la DÉFINITION, pas celle du
  // magasin de l'unité : c'est elle que le bloc rend, et la seule que l'on
  // sache réécrire. `value` (ce que le registre d'éditeurs passe) est ignoré
  // pour cette raison.
  const initial = typeof seedEndpoint === 'string' && seedEndpoint !== ''
    ? seedEndpoint
    : (typeof value === 'string' ? value : NONE)

  const [endpoint, setEndpoint] = useState(initial)
  const [applied, setApplied] = useState(initial)
  const [state, setState] = useState(null)
  const [error, setError] = useState(null)
  const [saving, setSaving] = useState(false)
  // Relance la lecture après une écriture réussie : la liste des formulaires du
  // NOUVEAU processus vient d'être changée par cette écriture même, et sans cela
  // le panneau afficherait l'état d'avant — celui-ci absent de sa propre liste.
  const [reloadKey, setReloadKey] = useState(0)
  // La liste des formulaires est une pop-in de plus, ouverte depuis le résumé.
  const [formsOpen, setFormsOpen] = useState(false)
  const panelRef = useRef(null)
  const closeRef = useRef(null)

  const dirty = endpoint !== applied
  // Le chemin ACTUELLEMENT appliqué est-il une route de code ? Lu sur le chemin
  // appliqué, pas sur celui qu'on choisit — c'est ce qu'on QUITTE qui compte.
  const leavingRoute = applied !== '' && !applied.startsWith('/eca/')
  const canWrite = Boolean(defNid && field)
  // Y a-t-il un canevas à montrer ? Sans flux, le plein écran n'ouvrirait qu'un
  // vide de mille pixels : le panneau se replie alors sur un dialogue ordinaire,
  // et ne s'agrandit que quand il a un schéma à afficher.
  const hasCanvas = Boolean(state?.current)

  // Le registre d'Échap classe par rang de rendu : la liste des formulaires,
  // montée après ce panneau, prend la touche seule quand elle est ouverte.
  useEscapeClose(() => onClose(), true)

  // Focus initial DANS le panneau, et piège à focus : sans eux on tabule dans le
  // canevas figé derrière la modale (même geste que la médiathèque).
  useEffect(() => { closeRef.current?.focus() }, [])
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key !== 'Tab') return
      // Une pop-in posée par-dessus a SON piège (sur son propre panneau). Celui-ci
      // écoute `document` en CAPTURE : il passerait devant et ramènerait le focus
      // ici à chaque Tab, rendant la liste inparcourable au clavier.
      if (formsOpen) return
      const focusables = panelRef.current?.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea, [tabindex]:not([tabindex="-1"])',
      )
      if (!focusables?.length) return
      const first = focusables[0]
      const last = focusables[focusables.length - 1]
      if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus() }
      else if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus() }
    }
    document.addEventListener('keydown', onKeyDown, true)
    return () => document.removeEventListener('keydown', onKeyDown, true)
  }, [formsOpen])

  useEffect(() => {
    let cancelled = false
    setError(null)
    fetch(`/api/builder/processes?endpoint=${encodeURIComponent(endpoint)}&limit=2`, { credentials: 'include' })
      .then(r => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then(data => { if (!cancelled) setState(data) })
      .catch(() => { if (!cancelled) setError('Impossible de lire les processus.') })
    return () => { cancelled = true }
  }, [endpoint, reloadKey])

  // Le sélecteur liste les endpoints, pas les modèles : deux flux qui répondent
  // au même chemin sont un accident de configuration, et c'est le CHEMIN que la
  // définition stocke.
  const options = useMemo(() => {
    const rows = state?.processes ?? []
    const seen = new Set()
    const out = []
    for (const row of rows) {
      if (row.wildcard || seen.has(row.endpoint)) continue
      seen.add(row.endpoint)
      out.push(row)
    }
    // Un chemin déjà posé sur la définition que la liste ECA ne contient pas doit
    // rester sélectionnable : sans cette ligne, le <select> retomberait
    // silencieusement sur « Aucun » et le prochain « Appliquer » l'effacerait.
    // C'est le cas des formulaires servis par une route de CODE — il faut
    // pouvoir y revenir après avoir regardé ailleurs.
    if (endpoint && !seen.has(endpoint)) {
      out.unshift({
        id: endpoint,
        endpoint,
        label: state?.kind === 'route' ? 'Route de l’application' : 'Chemin sans processus',
        status: true,
      })
    }
    return out
  }, [state, endpoint])

  const apply = useCallback(async () => {
    if (!canWrite) return
    setSaving(true)
    setError(null)
    try {
      const token = await ensureCsrfToken()
      const res = await fetch('/api/builder/def/process', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': token },
        body: JSON.stringify({ nid: defNid, field, endpoint }),
      })
      const body = await res.json().catch(() => null)
      if (!res.ok) throw new Error(body?.message ?? `Erreur ${res.status}`)
      setApplied(endpoint)
      setReloadKey(k => k + 1)
    } catch (e) {
      setError(e.message)
    } finally {
      setSaving(false)
    }
  }, [canWrite, defNid, field, endpoint])

  return createPortal(
    <div className="modal-scrim modal-scrim-full">
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="builder-process-title"
        className={`modal-panel flex flex-col overflow-hidden ${hasCanvas ? 'modal-panel-full' : 'w-full max-w-3xl'}`}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex flex-col gap-3 border-b border-slate-200 px-5 py-3">
          <div className="flex items-start justify-between gap-4">
            <h2 id="builder-process-title" className="flex items-center gap-2 text-base font-semibold text-slate-800">
              <Workflow size={17} aria-hidden="true" />
              {label}
            </h2>
            <div className="flex items-center gap-2">
              {dirty && canWrite && (
                <button type="button" onClick={apply} disabled={saving} className="btn btn-primary btn-sm">
                  {saving ? 'Enregistrement…' : 'Appliquer le changement'}
                </button>
              )}
              {state?.current && (
                <a
                  href={`/eca-editor/${state.current.id}`}
                  target="_blank"
                  rel="noopener"
                  data-builder-allow-navigation=""
                  className="btn btn-secondary btn-sm"
                >
                  <ExternalLink size={13} aria-hidden="true" />
                  Ouvrir dans un onglet
                  <span className="sr-only"> (nouvelle fenêtre)</span>
                </a>
              )}
              <button
                ref={closeRef}
                type="button"
                onClick={onClose}
                className="p-1 rounded text-slate-500 hover:text-slate-800 hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500"
                aria-label="Fermer le panneau Processus"
              >
                <X size={16} aria-hidden="true" />
              </button>
            </div>
          </div>

          <div className="flex flex-wrap items-start gap-x-6 gap-y-3">
            <div className="flex flex-col gap-1">
              <label htmlFor="builder-process-select" className="field-label">
                Processus rattaché à ce formulaire
              </label>
              <select
                id="builder-process-select"
                className="select"
                value={endpoint}
                disabled={!canWrite}
                onChange={(e) => setEndpoint(e.target.value)}
              >
                <option value={NONE}>Aucun</option>
                {options.map(opt => (
                  <option key={opt.endpoint} value={opt.endpoint}>
                    {opt.label} — {opt.endpoint}
                  </option>
                ))}
              </select>
              {/* Rien à dire tant que le réglage est appliqué : seul l'écart
                  entre ce qui est choisi et ce qui est enregistré mérite une
                  ligne. */}
              {dirty && (
                <p className="text-xs text-slate-600" role="status">
                  Non appliqué : ce formulaire poste encore sur {applied || '— rien —'}.
                </p>
              )}
              {/* Quitter une route de CODE pour un flux ECA n'est pas un simple
                  rebranchement : le traitement, la protection anti-robot et le
                  format attendu du corps changent avec elle. */}
              {dirty && leavingRoute && (
                <p className="alert alert-warning text-xs" role="alert">
                  <AlertTriangle size={14} aria-hidden="true" className="mt-0.5 shrink-0" />
                  <span>Ce formulaire est aujourd’hui traité par une route de l’application. La quitter change le traitement des saisies — et leur protection anti-robot.</span>
                </p>
              )}
            </div>

            <div className="min-w-[18rem] max-h-56 flex-1 overflow-y-auto pr-1" aria-live="polite">
              {error && <p className="text-sm text-rose-700" role="alert">{error}</p>}
              {state && (
                <ProcessSummary
                  state={state}
                  endpoint={endpoint}
                  onShowForms={() => setFormsOpen(true)}
                />
              )}
            </div>
          </div>
        </div>

        {hasCanvas ? (
          <div className="min-h-0 flex-1">
            <Suspense fallback={<div className="flex h-full items-center justify-center text-sm text-slate-500">Chargement du flux…</div>}>
              {/* `key` : l'éditeur ECA charge son modèle À SON MONTAGE. Sans
                  remontage, changer de processus dans le sélecteur laisserait le
                  canevas afficher le flux précédent, sans erreur. */}
              <EcaEditor
                key={state.current.id}
                modelId={state.current.id}
                embedded
                onBack={onClose}
              />
            </Suspense>
          </div>
        ) : (
          // Sans flux à montrer, le message DÉPEND de la raison — et une route
          // servie par du code n'est pas une panne : dire « aucun flux ne répond »
          // ferait chercher un problème là où le câblage est valide.
          <p className="px-5 py-4 text-sm text-slate-600">
            {state?.kind === 'route' ? (
              <>Ce formulaire est traité par du code de l’application, pas par un flux ECA :
              il n’y a pas de schéma à afficher ni à modifier ici. Le câblage est valide —
              choisir un processus ci-dessus le remplacerait.</>
            ) : endpoint ? (
              <>Aucun flux ECA ne répond à <code className="rounded bg-slate-100 px-1.5 py-0.5 text-2xs">{endpoint}</code> :
              en l’état, les saisies de ce formulaire partiraient dans le vide.</>
            ) : (
              <>Choisissez un processus ci-dessus pour voir le flux qui traitera les saisies.</>
            )}
          </p>
        )}
      </div>

      {formsOpen && (
        <ProcessFormsDialog endpoint={endpoint} onClose={() => setFormsOpen(false)} />
      )}
    </div>,
    document.body,
  )
}
